<section
  class="workflow-version-workbench"
  aria-labelledby="workflow-version-workbench-title"
  data-testid="workflow-version-workspace"
>
  <ng-container *ngIf="definition as currentDefinition; else noDefinition">
    <div class="workflow-version-workbench__sticky">
      <header class="workflow-version-workbench__command-bar">
        <button
          uiButton
          #backButton
          type="button"
          class="workflow-version-workbench__back"
          data-testid="back-to-workflow-registry"
          (click)="requestBack()"
        >
          <i class="pi pi-arrow-left" aria-hidden="true"></i>
          <span>Back to workflow registry</span>
        </button>

        <div class="workflow-version-workbench__identity">
          <h2
            #workspaceHeading
            id="workflow-version-workbench-title"
            tabindex="-1"
          >
            {{
              currentDefinition.displayName ||
                formatCode(currentDefinition.workflowCode)
            }}
          </h2>
          <p>Version {{ currentDefinition.definitionVersionCode }}</p>
        </div>

        <div class="workflow-version-workbench__immutable-state" role="status">
          <i class="pi pi-lock" aria-hidden="true"></i>
          <span>Published version &middot; read only</span>
        </div>
        <div
          class="workflow-version-workbench__actions"
          *ngIf="
            (canTest &&
              currentDefinition.subjectTypeCode === 'hearing_cycle') ||
            (canEdit && currentDefinition.draftActionCode !== null)
          "
        >
          <details
            *ngIf="hearingSourceContext as context"
            #eligibilityMore
            class="workflow-version-workbench__more"
            data-hearing-more
            aria-label="More version actions"
            (toggle)="reconcileHearingMore(eligibilityMore)"
            (keydown.escape)="dismissHearingMore($event, context, eligibilityMore)"
          >
            <summary
              (click)="toggleHearingMore($event, context, eligibilityMore)"
              aria-controls="workflow-version-hearing-actions"
            >
              More
            </summary>
            <div
              id="workflow-version-hearing-actions"
              class="workflow-version-workbench__more-items"
            >
              <button
                uiButton
                #runHearingTest
                type="button"
                [wrap]="true"
                [disabled]="loading || interactionBlocked || lifecycleInProgress()"
                data-run-hearing-test
                aria-haspopup="dialog"
                (click)="openHearingTest(context, eligibilityMore, runHearingTest)"
              >
                Run eligibility test
              </button>
            </div>
          </details>
          <ng-container *ngIf="draftEditEntry() as draftEntry">
            <button
              uiButton
              #draftEditButton
              type="button"
              *ngIf="canEdit"
              [disabled]="
                loading ||
                !currentDefinition.draftActionCode ||
                interactionBlocked ||
                lifecycleInProgress()
              "
              data-testid="edit-published-workflow"
              (click)="requestEditWorkflow(draftEntry)"
            >
              {{ draftActionLabel() }}
            </button>
          </ng-container>
        </div>
      </header>

      <nav
        class="workflow-version-workbench__navigation"
        aria-label="Version detail areas"
      >
        <button
          [wrap]="true"
          uiButton
          [text]="true"
          [outlined]="false"
          id="workflow-version-workbench-tab-map"
          type="button"
          [class.workflow-version-workbench__navigation-item--active]="
            activeView === 'map'
          "
          [attr.aria-current]="activeView === 'map' ? 'page' : null"
          aria-controls="workflow-version-workbench-panel-map"
          (click)="selectView('map')"
        >
          <i class="pi pi-sitemap" aria-hidden="true"></i>
          <span>Map</span>
        </button>
        <button
          [wrap]="true"
          uiButton
          [text]="true"
          [outlined]="false"
          id="workflow-version-workbench-tab-setup"
          type="button"
          [class.workflow-version-workbench__navigation-item--active]="
            activeView === 'setup'
          "
          [attr.aria-current]="activeView === 'setup' ? 'page' : null"
          aria-controls="workflow-version-workbench-panel-setup"
          (click)="selectView('setup')"
        >
          <i class="pi pi-cog" aria-hidden="true"></i>
          <span>Workflow settings</span>
        </button>
        <button
          [wrap]="true"
          uiButton
          [text]="true"
          [outlined]="false"
          id="workflow-version-workbench-tab-review"
          type="button"
          [class.workflow-version-workbench__navigation-item--active]="
            activeView === 'review'
          "
          [attr.aria-current]="activeView === 'review' ? 'page' : null"
          aria-controls="workflow-version-workbench-panel-review"
          (click)="selectView('review')"
        >
          <i class="pi pi-file-check" aria-hidden="true"></i>
          <span>Availability</span>
        </button>
      </nav>
    </div>

    <phms-hearing-eligibility-test
      *ngIf="canTest && currentDefinition.subjectTypeCode === 'hearing_cycle'"
      [definitionVersionId]="currentDefinition.id"
      [autoOpen]="false"
      presentation="modal"
      [sourceContext]="hearingSourceContext || ''"
      [currentSourceContext]="currentHearingSourceContext"
      [versionCode]="currentDefinition.definitionVersionCode"
      [interactionBlocked]="loading || interactionBlocked || lifecycleInProgress()"
    />
    <phms-workflow-entry-points
      *ngIf="entryPointsVisible"
      [workflowCode]="currentDefinition.workflowCode"
      [subjectTypeCode]="currentDefinition.subjectTypeCode"
      [interactionBlocked]="interactionBlocked || lifecycleInProgress()"
    />

    <section
      id="workflow-version-workbench-panel-map"
      class="workflow-version-workbench__panel workflow-version-workbench__panel--map"
      role="region"
      aria-labelledby="workflow-version-workbench-tab-map"
      [hidden]="activeView !== 'map'"
    >
      <h3 #mapHeading class="visually-hidden" tabindex="-1">
        Immutable workflow map
      </h3>

      <div
        class="workflow-version-map"
        [hidden]="workOpen() || presentationOpen() || routingOpen() || (statusOpen() && statusView() === 'map')"
        [attr.inert]="workOpen() || presentationOpen() || routingOpen() || (statusOpen() && statusView() === 'map') ? '' : null"
      >
      <ng-container *ngIf="graph() as currentGraph; else documentUnavailable">
        <p
          *ngIf="hasDocumentWarnings()"
          class="workflow-version-workbench__warning"
          role="status"
          data-testid="workflow-version-document-warning"
        >
          This immutable document contains
          {{ currentGraph.invalidElementCount }} invalid or unresolved graph
          {{ currentGraph.invalidElementCount === 1 ? "element" : "elements" }}
          and {{ currentGraph.malformedEntryCount }} malformed nested
          {{ currentGraph.malformedEntryCount === 1 ? "entry" : "entries" }}.
          They remain visible as saved evidence; this view does not repair or
          reinterpret them.
        </p>

        <phms-workflow-read-only-graph
          [graph]="currentGraph"
          [contextualInspector]="true"
          [selectionControlled]="true"
          [selectedRendererElementId]="selectedRendererElementId()"
          sourceContext="published-version"
          visualAriaLabel="Read-only diagram of the selected immutable workflow version"
          (selectionRequested)="onMapSelectionRequested($event)"
          (decisionGateRequested)="onDecisionGateRequested($event)"
          (decisionScopeRequested)="onDecisionScopeRequested($event)"
        >
          <div workflowGraphInspector class="workflow-version-inspector">
            <header class="workflow-version-inspector__header">
              <div>
                <p>{{ mapInspectorContextLabel() }}</p>
                <h3
                  #mapInspectorHeading
                  id="workflow-version-map-inspector-title"
                  tabindex="-1"
                >
                  {{ mapInspectorTitle() }}
                </h3>
              </div>
              <button
                [wrap]="true"
                uiButton
                [text]="true"
                [outlined]="false"
                *ngIf="mapSelection() || selectedDecisionGate()"
                type="button"
                class="workflow-version-inspector__context-button"
                (click)="showWholeWorkflow()"
              >
                Whole workflow
              </button>
            </header>

            <nav
              *ngIf="mapSelection()?.kind !== 'edge'"
              class="workflow-version-inspector__modes"
              aria-label="Immutable workflow inspector views"
            >
              <button
                [wrap]="true"
                [text]="true"
                [outlined]="false"
                uiButton
                type="button"
                [class.workflow-version-inspector__mode--active]="
                  inspectorPane() === 'details'
                "
                [attr.aria-current]="
                  inspectorPane() === 'details' ? 'page' : null
                "
                (click)="selectInspectorPane('details')"
              >
                Details
              </button>
              <button
                [wrap]="true"
                [text]="true"
                [outlined]="false"
                uiButton
                *ngIf="mapContextSupportsBehavior()"
                type="button"
                [class.workflow-version-inspector__mode--active]="
                  inspectorPane() === 'status-changes'
                "
                [attr.aria-current]="
                  inspectorPane() === 'status-changes' ? 'page' : null
                "
                [attr.aria-label]="
                  'View status changes, ' +
                  contextStatusChangeCount() +
                  (contextStatusChangeCount() === 1 ? ' entry' : ' entries')
                "
                (click)="selectInspectorPane('status-changes')"
              >
                Status changes
                <span>{{ contextStatusChangeCount() }}</span>
                <span class="visually-hidden">
                  {{ contextStatusChangeCount() === 1 ? "entry" : "entries" }}
                </span>
              </button>
              <button
                [wrap]="true"
                [text]="true"
                [outlined]="false"
                uiButton
                *ngIf="mapContextSupportsBehavior()"
                type="button"
                [class.workflow-version-inspector__mode--active]="
                  inspectorPane() === 'decisions'
                "
                [attr.aria-current]="
                  inspectorPane() === 'decisions' ? 'page' : null
                "
                [attr.aria-label]="
                  'View decisions, ' +
                  contextDecisionRuleCount() +
                  ' saved rule' +
                  (contextDecisionRuleCount() === 1 ? '' : 's')
                "
                (click)="selectInspectorPane('decisions')"
              >
                Decisions
                <span>{{ contextDecisionRuleCount() }}</span>
                <span class="visually-hidden">
                  saved rule{{ contextDecisionRuleCount() === 1 ? "" : "s" }}
                </span>
              </button>
            </nav>

            <div class="workflow-version-inspector__body">
              <section
                class="workflow-version-inspector__pane"
                [hidden]="inspectorPane() !== 'details'"
                aria-labelledby="workflow-version-map-inspector-title"
              >
                <div
                  *ngIf="!selectedGraphDetail(); else selectedElementDetails"
                  class="workflow-version-inspector__overview"
                >
                  <p>
                    Select a saved step or path to inspect it in context. This
                    view reads the immutable authored document and cannot change
                    the selected version.
                  </p>
                  <dl>
                    <div>
                      <dt>Work steps</dt>
                      <dd>{{ humanStepCount() }}</dd>
                    </div>
                    <div>
                      <dt>Map paths</dt>
                      <dd>{{ pathCount() }}</dd>
                    </div>
                    <div>
                      <dt>Status changes</dt>
                      <dd>{{ statusChangeCount() }}</dd>
                    </div>
                    <div>
                      <dt>Decision rules</dt>
                      <dd>{{ decisionRuleCount() }}</dd>
                    </div>
                  </dl>
                </div>

                <ng-template #selectedElementDetails>
                  <ng-container *ngIf="selectedGraphDetail() as detail">
                    <p class="workflow-version-inspector__description">
                      {{ detail.description }}
                    </p>
                    <div
                      class="workflow-version-inspector__entries"
                      role="group"
                      aria-label="Saved step sections"
                    >
                      <button
                        *ngIf="workEntry() as entry"
                        uiButton
                        type="button"
                        class="workflow-version-inspector__work"
                        data-inspect-published-work
                        [disabled]="loading || interactionBlocked || lifecycleInProgress()"
                        (click)="openPublishedWork(entry)"
                      >
                        Inspect Work
                      </button>
                      <ng-container *ngIf="presentationEntry() as entry">
                        <button
                          uiButton
                          type="button"
                          data-inspect-published-presentation="staffPage"
                          [disabled]="loading || interactionBlocked || lifecycleInProgress()"
                          (click)="openPublishedPresentation(entry, 'staffPage')"
                        >
                          Inspect Staff page
                        </button>
                        <button
                          uiButton
                          type="button"
                          data-inspect-published-presentation="dashboards"
                          [disabled]="loading || interactionBlocked || lifecycleInProgress()"
                          (click)="openPublishedPresentation(entry, 'dashboards')"
                        >
                          Inspect Dashboards
                        </button>
                      </ng-container>
                      <ng-container *ngIf="routingEntry() as entry">
                        <button
                          *ngIf="entry.target.kind === 'node'"
                          uiButton
                          type="button"
                          data-inspect-published-routing="node"
                          [disabled]="loading || interactionBlocked || lifecycleInProgress()"
                          (click)="openPublishedRouting(entry)"
                        >
                          Inspect routing and results
                        </button>
                      </ng-container>
                      <button
                        *ngIf="nodeStatusEntry() as entry"
                        uiButton
                        type="button"
                        [wrap]="true"
                        data-inspect-published-status="node"
                        [disabled]="loading || interactionBlocked || lifecycleInProgress()"
                        (click)="openPublishedStatus(entry)"
                      >
                        Inspect labels and rules
                      </button>
                    </div>
                    <dl class="workflow-version-inspector__facts">
                      <div *ngFor="let fact of detail.facts">
                        <dt>{{ fact.label }}</dt>
                        <dd>{{ fact.value }}</dd>
                      </div>
                    </dl>
                  </ng-container>
                </ng-template>
              </section>

              <section
                class="workflow-version-inspector__pane"
                [hidden]="inspectorPane() !== 'path'"
                aria-label="Immutable map path"
              >
                <div
                  *ngIf="routingEntry() as entry"
                  class="workflow-version-inspector__entries"
                >
                  <button
                    *ngIf="entry.target.kind === 'edge'"
                    uiButton
                    type="button"
                    data-inspect-published-routing="edge"
                    [disabled]="loading || interactionBlocked || lifecycleInProgress()"
                    (click)="openPublishedRouting(entry)"
                  >
                    Inspect routing and results
                  </button>
                </div>
                <phms-platform-workflow-draft-edge-editor
                  [contextual]="true"
                  [inspectionMode]="true"
                  [document]="definitionDocument()"
                  [canAuthor]="false"
                  [interactionBlocked]="false"
                  [resetContextEdgeIndex]="selectedPathIndex()"
                  [resetVersion]="structuredInspectorResetVersion()"
                ></phms-platform-workflow-draft-edge-editor>
              </section>

              <section
                class="workflow-version-inspector__pane"
                [hidden]="inspectorPane() !== 'status-changes'"
                aria-label="Immutable status changes"
              >
                <phms-platform-workflow-draft-transition-editor
                  [contextual]="true"
                  [inspectionMode]="true"
                  [document]="definitionDocument()"
                  [canAuthor]="false"
                  [interactionBlocked]="false"
                  [resetContextStepNodeId]="selectedHumanStepNodeId()"
                  [resetVersion]="structuredInspectorResetVersion()"
                  [stateCodes]="savedReferenceCodes().stateCodes"
                  [roleCodes]="savedReferenceCodes().roleCodes"
                  [queueCodes]="savedReferenceCodes().queueCodes"
                  [managerNotificationTargetCodes]="
                    savedReferenceCodes().managerNotificationTargetCodes
                  "
                  [externalNotificationTargetCodes]="
                    savedReferenceCodes().externalNotificationTargetCodes
                  "
                  (decisionRulesRequested)="
                    onTransitionDecisionRulesRequested($event)
                  "
                ></phms-platform-workflow-draft-transition-editor>
              </section>

              <section
                class="workflow-version-inspector__pane"
                [hidden]="inspectorPane() !== 'decisions'"
                aria-label="Immutable decision rules"
              >
                <phms-platform-workflow-draft-decision-editor
                  [contextual]="true"
                  [inspectionMode]="true"
                  [document]="definitionDocument()"
                  [canAuthor]="false"
                  [interactionBlocked]="false"
                  [resetContextStepNodeId]="selectedHumanStepNodeId()"
                  [resetVersion]="structuredInspectorResetVersion()"
                  [decisionKindCodes]="savedReferenceCodes().decisionKindCodes"
                  [decisionOutcomeCodes]="
                    savedReferenceCodes().decisionOutcomeCodes
                  "
                  [decisionReactionCodes]="
                    savedReferenceCodes().decisionReactionCodes
                  "
                  [stateCodes]="savedReferenceCodes().stateCodes"
                  [roleCodes]="savedReferenceCodes().roleCodes"
                  [queueCodes]="savedReferenceCodes().queueCodes"
                  [managerNotificationTargetCodes]="
                    savedReferenceCodes().managerNotificationTargetCodes
                  "
                  [externalNotificationTargetCodes]="
                    savedReferenceCodes().externalNotificationTargetCodes
                  "
                ></phms-platform-workflow-draft-decision-editor>
              </section>
            </div>
          </div>
        </phms-workflow-read-only-graph>
      </ng-container>

      <ng-template #documentUnavailable>
        <section
          class="workflow-version-document-fallback"
          data-testid="workflow-version-document-fallback"
          aria-labelledby="workflow-version-document-fallback-title"
        >
          <div class="workflow-version-document-fallback__message">
            <i class="pi pi-info-circle" aria-hidden="true"></i>
            <div>
              <h3 id="workflow-version-document-fallback-title">
                {{ definitionDocumentFallbackHeading() }}
              </h3>
              <p>{{ definitionDocumentFallbackMessage() }}</p>
            </div>
          </div>

          <dl
            class="workflow-version-document-fallback__evidence"
            aria-label="Current runtime diagnostics"
          >
            <div>
              <dt>Current diagnostic steps</dt>
              <dd>{{ currentDefinition.stepDefinitions.length }}</dd>
            </div>
            <div>
              <dt>Current diagnostic transitions</dt>
              <dd>{{ currentDefinition.workflowTransitions.length }}</dd>
            </div>
            <div>
              <dt>Current diagnostic requirements</dt>
              <dd>{{ currentDefinition.requiredDecisions.length }}</dd>
            </div>
          </dl>
          <p class="workflow-version-document-fallback__boundary">
            These flattened values come from the current runtime diagnostics
            evaluation. They are shown only as a fallback and are not used to
            invent authored nodes, paths, ordering, labels, scope, or
            notifications for this version.
          </p>
          <details class="workflow-version-workbench__technical">
            <summary>Technical document status</summary>
            <dl>
              <div>
                <dt>Status code</dt>
                <dd>
                  <code>{{ definitionDocumentTechnicalStatus() }}</code>
                </dd>
              </div>
              <div *ngIf="definitionDocumentIssueCodes().length > 0">
                <dt>Issue codes</dt>
                <dd>
                  <code
                    *ngFor="let issueCode of definitionDocumentIssueCodes()"
                  >
                    {{ issueCode }}
                  </code>
                </dd>
              </div>
            </dl>
          </details>
        </section>
      </ng-template>
      </div>
      <ng-container *ngIf="workOpen()">
        <section *ngIf="publishedWork() as work" class="workflow-version-work" aria-labelledby="published-work-title" data-published-work>
          <header class="workflow-version-work__header">
            <button uiButton type="button" [text]="true" data-back-to-published-map
              [disabled]="loading || interactionBlocked || lifecycleInProgress()" (click)="backToPublishedMap(work.context)">
              <i class="pi pi-arrow-left" aria-hidden="true"></i> Back to process map
            </button>
            <div>
              <p>{{ work.provenance.workflowCode }} · {{ work.provenance.definitionVersionCode }}</p>
              <h3 #publishedWorkHeading id="published-work-title" tabindex="-1">{{ work.node?.label || 'Work inspection' }}</h3>
              <p>Saved Work configuration. Current assignments and completion evidence are separate.</p>
            </div>
          </header>
          <details class="workflow-version-work__technical">
            <summary>Saved version identity</summary>
            <p>{{ work.provenance.statement }}</p>
            <dl class="workflow-version-work__facts">
              <div><dt>Publication ID</dt><dd>{{ work.provenance.id }}</dd></div>
              <div><dt>Registry document status</dt><dd>{{ work.provenance.documentStatusCode }}</dd></div>
              <div><dt>Reported canonical hash</dt><dd><code>{{ work.provenance.canonicalContentHash }}</code></dd></div>
              <div><dt>Payload / compiler</dt><dd>{{ work.provenance.payloadSchemaVersion }} / {{ work.provenance.compilerVersion }}</dd></div>
            </dl>
            <ul *ngIf="work.provenance.issueCodes.length"><li *ngFor="let code of work.provenance.issueCodes"><code>{{ code }}</code></li></ul>
          </details>
          <div *ngIf="work.state === 'unavailable'" role="status" data-published-work-unavailable>
            <p *ngFor="let issue of work.issues">{{ issue }}</p>
          </div>
          <ng-container *ngIf="work.state === 'available'">
            <p *ngFor="let issue of work.issues" role="status">{{ issue }}</p>
            <p *ngFor="let issue of work.node?.issues" role="status">{{ issue }}</p>
            <ui-tab-group class="workflow-version-work__tabs" [stretchTabs]="false" [navigationTabs]="workTabs"
              [activeTabId]="workTask()" navigationAriaLabel="Saved Work sections" (tabRequested)="selectPublishedWorkTask($event, work.context)">
              <section class="workflow-version-work__body" aria-labelledby="published-work-task-title" [attr.data-published-work-task]="workTask()">
                <h4 #publishedWorkTaskHeading id="published-work-task-title" tabindex="-1">{{ workTaskLabel() }}</h4>
                <ng-container *ngIf="workTask() !== 'progress'; else savedProgress">
                  <ng-container
                    *ngIf="publishedWorkTaskSection() as section; else stepPolicyUnavailable"
                    [ngTemplateOutlet]="publishedWorkSection"
                    [ngTemplateOutletContext]="{ $implicit: section }"
                  />
                  <ng-template #stepPolicyUnavailable>
                    <div role="status" data-published-step-policy-unavailable>
                      <p>
                        Saved policy content is withheld because its source could not be matched
                        to this Work inspection.
                      </p>
                      <p *ngFor="let issue of publishedStepPolicies()?.issues">{{ issue }}</p>
                    </div>
                  </ng-template>
                </ng-container>
                <ng-template #savedProgress>
                  <ng-container
                    *ngIf="publishedProgressSections() as sections; else progressUnavailable"
                  >
                    @for (section of sections; track section.id) {
                      <section
                        [attr.aria-label]="section.label"
                        [attr.data-published-progress-section]="section.id"
                      >
                        <h5>{{ section.label }}</h5>
                        <ng-container
                          [ngTemplateOutlet]="publishedWorkSection"
                          [ngTemplateOutletContext]="{ $implicit: section }"
                        />
                      </section>
                    }
                  </ng-container>
                  <ng-template #progressUnavailable>
                    <div role="status" data-published-progress-unavailable>
                      <p>
                        Saved progress and labels are withheld because their source could not be
                        matched to this Work inspection.
                      </p>
                      <p *ngFor="let issue of publishedProgress()?.issues">{{ issue }}</p>
                    </div>
                  </ng-template>
                </ng-template>
                <details *ngIf="workTask() === 'actions'" class="workflow-version-work__other" data-published-other-commands>
                  <summary>Other saved commands</summary>
                  <ng-container [ngTemplateOutlet]="publishedWorkSection" [ngTemplateOutletContext]="{ $implicit: work.sections.otherCommands }" />
                </details>
              </section>
            </ui-tab-group>
          </ng-container>
        </section>
      </ng-container>
      <ng-container *ngIf="presentationOpen()">
        <section
          *ngIf="publishedPresentation() as presentation"
          class="workflow-version-work workflow-version-presentation"
          aria-labelledby="published-presentation-title"
          data-published-presentation
        >
          <header class="workflow-version-work__header">
            <button
              uiButton
              type="button"
              [text]="true"
              data-back-to-published-presentation-map
              [disabled]="loading || interactionBlocked || lifecycleInProgress()"
              (click)="backToPublishedPresentationMap(presentation.context)"
            >
              <i class="pi pi-arrow-left" aria-hidden="true"></i> Back to process map
            </button>
            <div>
              <p>
                {{ presentation.provenance.workflowCode }} ·
                {{ presentation.provenance.definitionVersionCode }}
              </p>
              <h3
                #publishedPresentationHeading
                id="published-presentation-title"
                tabindex="-1"
              >
                {{ presentation.node?.label || "Saved presentation" }}
              </h3>
              <p>Saved Staff page and Dashboard configuration for this version.</p>
            </div>
          </header>
          <details class="workflow-version-work__technical">
            <summary>Saved version identity</summary>
            <p>{{ presentation.provenance.statement }}</p>
            <dl class="workflow-version-work__facts">
              <div>
                <dt>Publication ID</dt>
                <dd>{{ presentation.provenance.id }}</dd>
              </div>
              <div>
                <dt>Registry document status</dt>
                <dd>{{ presentation.provenance.documentStatusCode }}</dd>
              </div>
              <div>
                <dt>Reported canonical hash</dt>
                <dd>
                  <code>{{ presentation.provenance.canonicalContentHash }}</code>
                </dd>
              </div>
              <div>
                <dt>Payload / compiler</dt>
                <dd>
                  {{ presentation.provenance.payloadSchemaVersion }} /
                  {{ presentation.provenance.compilerVersion }}
                </dd>
              </div>
            </dl>
            <ul *ngIf="presentation.provenance.issueCodes.length">
              <li *ngFor="let code of presentation.provenance.issueCodes">
                <code>{{ code }}</code>
              </li>
            </ul>
          </details>
          <div
            *ngIf="presentation.state === 'unavailable'"
            role="status"
            data-published-presentation-unavailable
          >
            <p *ngFor="let issue of presentation.issues">{{ issue }}</p>
          </div>
          <ng-container *ngIf="presentation.state === 'available'">
            <p *ngFor="let issue of presentation.issues" role="status">{{ issue }}</p>
            <p *ngFor="let issue of presentation.node?.issues" role="status">
              {{ issue }}
            </p>
            <ui-tab-group
              class="workflow-version-work__tabs"
              [stretchTabs]="false"
              [navigationTabs]="presentationTabs"
              [activeTabId]="presentationTask()"
              navigationAriaLabel="Saved presentation sections"
              (tabRequested)="
                selectPublishedPresentationTask($event, presentation.context)
              "
            >
              <section
                class="workflow-version-work__body"
                aria-labelledby="published-presentation-task-title"
                [attr.data-published-presentation-task]="presentationTask()"
              >
                <h4
                  #publishedPresentationTaskHeading
                  id="published-presentation-task-title"
                  tabindex="-1"
                >
                  {{ presentationTaskLabel() }}
                </h4>
                <ng-container
                  [ngTemplateOutlet]="publishedPresentationSection"
                  [ngTemplateOutletContext]="{
                    $implicit: presentation.sections[presentationTask()],
                  }"
                />
              </section>
            </ui-tab-group>
            <section
              *ngIf="presentation.pins as pins"
              data-published-presentation-pins
            >
              <ng-container
                [ngTemplateOutlet]="publishedPresentationGroup"
                [ngTemplateOutletContext]="{ $implicit: pins }"
              />
            </section>
          </ng-container>
        </section>
      </ng-container>
      <ng-container *ngIf="routingOpen()">
        <section
          *ngIf="publishedRouting() as routing"
          class="workflow-version-work workflow-version-routing"
          aria-labelledby="published-routing-title"
          data-published-routing
          [attr.data-routing-target-kind]="routing.target.kind"
        >
          <header class="workflow-version-work__header">
            <button
              uiButton
              type="button"
              [text]="true"
              data-back-to-published-routing-map
              [disabled]="loading || interactionBlocked || lifecycleInProgress()"
              (click)="backToPublishedRoutingMap(routing.context)"
            >
              <i class="pi pi-arrow-left" aria-hidden="true"></i> Back to process map
            </button>
            <div>
              <p>
                {{ routing.provenance.workflowCode }} ·
                {{ routing.provenance.definitionVersionCode }}
              </p>
              <h3 #publishedRoutingHeading id="published-routing-title" tabindex="-1">
                {{ routing.element?.label || 'Routing and results' }}
              </h3>
              <p>
                Saved routing and results. Current execution and route availability
                are separate.
              </p>
            </div>
          </header>
          <details class="workflow-version-work__technical">
            <summary>Saved version identity</summary>
            <p>{{ routing.provenance.statement }}</p>
            <dl class="workflow-version-work__facts">
              <div>
                <dt>Publication ID</dt>
                <dd>{{ routing.provenance.id }}</dd>
              </div>
              <div>
                <dt>Registry document status</dt>
                <dd>{{ routing.provenance.documentStatusCode }}</dd>
              </div>
              <div>
                <dt>Reported canonical hash</dt>
                <dd>
                  <code>{{ routing.provenance.canonicalContentHash }}</code>
                </dd>
              </div>
              <div>
                <dt>Payload / compiler</dt>
                <dd>
                  {{ routing.provenance.payloadSchemaVersion }} /
                  {{ routing.provenance.compilerVersion }}
                </dd>
              </div>
            </dl>
            <ul *ngIf="routing.provenance.issueCodes.length">
              <li *ngFor="let code of routing.provenance.issueCodes">
                <code>{{ code }}</code>
              </li>
            </ul>
          </details>
          <div
            *ngIf="routing.state === 'unavailable'"
            role="status"
            data-published-routing-unavailable
          >
            <p *ngFor="let issue of routing.issues">{{ issue }}</p>
          </div>
          <ng-container *ngIf="routing.state === 'available'">
            <p *ngFor="let issue of routing.issues" role="status">{{ issue }}</p>
            <p *ngFor="let issue of routing.element?.issues" role="status">
              {{ issue }}
            </p>
            <p *ngFor="let issue of routing.node?.issues" role="status">
              {{ issue }}
            </p>
            <details *ngIf="routing.element as element" class="workflow-version-work__raw" data-published-routing-element-raw>
              <summary>Original saved element</summary>
              <pre>{{ element.raw | json }}</pre>
            </details>
            <ng-container *ngIf="routing.node as source">
              <details *ngIf="routing.target.kind === 'edge'" class="workflow-version-work__raw" data-published-routing-source-raw>
                <summary>Original saved source node</summary>
                <p>{{ source.label }} · Node ID {{ source.id }} · Saved index {{ source.index }}</p>
                <pre>{{ source.raw | json }}</pre>
              </details>
            </ng-container>
            <ui-tab-group
              class="workflow-version-work__tabs"
              [stretchTabs]="false"
              [navigationTabs]="routingTabs"
              [activeTabId]="routingTask()"
              navigationAriaLabel="Saved routing and results sections"
              (tabRequested)="selectPublishedRoutingTask($event, routing.context)"
            >
              <section
                class="workflow-version-work__body"
                aria-labelledby="published-routing-task-title"
                [attr.data-published-routing-task]="routingTask()"
              >
                <h4
                  #publishedRoutingTaskHeading
                  id="published-routing-task-title"
                  tabindex="-1"
                >
                  {{ routingTaskLabel() }}
                </h4>
                @for (section of publishedRoutingSections(); track section.id) {
                  <section
                    [attr.aria-label]="section.label"
                    [attr.data-published-routing-section]="section.id"
                  >
                    <h5>{{ section.label }}</h5>
                    <ng-container
                      [ngTemplateOutlet]="publishedWorkSection"
                      [ngTemplateOutletContext]="{ $implicit: section }"
                    />
                  </section>
                }
              </section>
            </ui-tab-group>
          </ng-container>
        </section>
      </ng-container>
      <ng-container *ngIf="statusOpen() && statusView() === 'map'">
        <ng-container [ngTemplateOutlet]="publishedStatusTemplate" [ngTemplateOutletContext]="{ $implicit: publishedStatus() }" />
      </ng-container>
    </section>

    <section
      id="workflow-version-workbench-panel-setup"
      class="workflow-version-workbench__panel"
      role="region"
      aria-labelledby="workflow-version-workbench-tab-setup"
      [hidden]="activeView !== 'setup'"
    >
      <div class="workflow-version-settings-frame" [hidden]="statusOpen() && statusView() === 'setup'" [attr.inert]="statusOpen() && statusView() === 'setup' ? '' : null">
      <div class="workflow-version-workbench__panel-heading">
        <div>
          <h3 #setupHeading tabindex="-1">Workflow settings</h3>
          <p>
            Saved identity, scope, configuration, and instance behavior for this
            version.
          </p>
        </div>
        <button
          *ngIf="workflowStatusEntry() as entry"
          uiButton
          type="button"
          [wrap]="true"
          data-inspect-published-status="workflow"
          [disabled]="loading || interactionBlocked || lifecycleInProgress()"
          (click)="openPublishedStatus(entry)"
        >
          Inspect workflow labels and rules
        </button>
        <span>Immutable</span>
      </div>

      <ng-container *ngIf="publishedInspection() as inspection">
        <p class="workflow-version-settings__provenance" data-published-provenance>{{ inspection.provenance.statement }}</p>
        <div *ngIf="inspection.state === 'unavailable'" role="status" data-published-settings-unavailable>
          <p>Registry document status: <code>{{ inspection.provenance.documentStatusCode }}</code></p>
          <p *ngFor="let issue of inspection.issues">{{ issue }}</p>
          <ul *ngIf="inspection.provenance.issueCodes.length"><li *ngFor="let code of inspection.provenance.issueCodes"><code>{{ code }}</code></li></ul>
        </div>
        <ng-container *ngIf="publishedWorkflowSettings() as savedWorkflow">
        <ui-tab-group class="workflow-version-settings" [stretchTabs]="false" [navigationTabs]="settingsTabs"
          [activeTabId]="settingsTask()" navigationAriaLabel="Saved workflow settings"
          (tabRequested)="selectSettingsTask($event, inspection.context, savedWorkflow.context)">
          <section class="workflow-version-settings__body" aria-labelledby="published-settings-task-title" data-published-settings>
            <h4 #savedSettingsHeading id="published-settings-task-title" tabindex="-1">{{ settingsTaskLabel() }}</h4>
            <ng-container *ngIf="settingsTask() === 'general'">
              <ng-container *ngIf="inspection.state === 'available'">
                <dl class="workflow-version-setup" data-testid="workflow-version-setup" data-published-general>
                  @for (row of inspection.general.rows; track row.id) {
                    <div [attr.data-saved-general-field]="row.id"><dt>{{ row.label }}</dt><dd>{{ row.value }}
                      <small *ngFor="let issue of row.issues">{{ issue }}</small>
                    </dd></div>
                  }
                </dl>
                <details class="workflow-version-workbench__technical">
                  <summary>Saved General values</summary><pre>{{ inspection.general.raw | json }}</pre>
                </details>
              </ng-container>
            </ng-container>
            <ng-container *ngIf="settingsTask() === 'data'">
              <p>Shared values declared in this saved version.</p>
              <p *ngFor="let issue of inspection.data.issues" role="status">{{ issue }}</p>
              <ng-container *ngIf="inspection.state === 'available'">
                <p *ngIf="inspection.data.state === 'absent'" data-published-data-empty>{{ inspection.data.presence === 'null' ? 'The saved shared-data schema is null.' : 'No shared-data fields are recorded in this saved document.' }}</p>
                <p *ngIf="inspection.data.state === 'empty'" data-published-data-empty>The saved shared-data field list is empty.</p>
                <table *ngIf="inspection.data.rows.length" class="workflow-version-data" aria-label="Saved shared data" data-published-data>
                  <thead><tr><th scope="col">Name</th><th scope="col">Key</th><th scope="col">Type</th><th scope="col">Required at start</th><th scope="col">Record type</th></tr></thead>
                  <tbody>
                    @for (field of inspection.data.rows; track field.id) {
                      <tr [attr.data-saved-data-index]="field.index"><td>{{ field.label }}<small *ngFor="let issue of field.issues">{{ issue }}</small></td>
                        <td><code>{{ field.key }}</code></td><td>{{ field.typeLabel }}</td><td>{{ field.requiredAtStartLabel }}</td><td>{{ field.recordTypeLabel }}</td></tr>
                    }
                  </tbody>
                </table>
                <details *ngIf="inspection.data.presence !== 'absent'" class="workflow-version-workbench__technical" data-published-data-raw>
                  <summary>Saved shared-data schema</summary><pre>{{ inspection.data.raw | json }}</pre>
                </details>
              </ng-container>
            </ng-container>
            <ng-container *ngIf="settingsTask() === 'assignment'">
              <ng-container *ngIf="inspection.state === 'available'">
      <phms-workflow-assignment-policy
        [document]="definitionDocument()"
        [canAuthor]="false"
        [inspectionMode]="true"
        [roleCodes]="savedReferenceCodes().roleCodes"
        [queueCodes]="savedReferenceCodes().queueCodes"
        [resetVersion]="structuredInspectorResetVersion()"
      ></phms-workflow-assignment-policy>

              </ng-container>
            </ng-container>
            <ng-container
              *ngIf="
                settingsTask() === 'publicReferences' ||
                settingsTask() === 'eventAutomation' ||
                settingsTask() === 'dependencies' ||
                settingsTask() === 'navigation'
              "
            >
              <p
                *ngIf="settingsTask() === 'eventAutomation'"
                data-published-events-boundary
              >
                Saved event and action declarations do not establish current dispatch
                support.
              </p>
              <ng-container
                *ngIf="
                  publishedWorkflowSettingsSection() as section;
                  else savedWorkflowUnavailable
                "
              >
                <section class="workflow-version-work" [attr.data-published-workflow-settings-section]="section.id">
                  <p *ngFor="let issue of savedWorkflow.issues" role="status">
                    {{ issue }}
                  </p>
                  <ng-container
                    [ngTemplateOutlet]="publishedWorkSection"
                    [ngTemplateOutletContext]="{ $implicit: section }"
                  />
                </section>
              </ng-container>
              <ng-template #savedWorkflowUnavailable>
                <div role="status" data-published-workflow-settings-unavailable>
                  <p *ngFor="let issue of savedWorkflow.issues">{{ issue }}</p>
                </div>
              </ng-template>
            </ng-container>
            <ng-container *ngIf="settingsTask() === 'technical'">
              <p>Identity reported for this registry version.</p>
      <details class="workflow-version-workbench__technical">
        <summary>Technical identity</summary>
        <dl>
          <div>
            <dt>Definition ID</dt>
            <dd>
              <code>{{ currentDefinition.id }}</code>
            </dd>
          </div>
          <div>
            <dt>Configuration version ID</dt>
            <dd>
              <code>{{ currentDefinition.configurationVersionId }}</code>
            </dd>
          </div>
          <div>
            <dt>Compiler version</dt>
            <dd>
              <code>{{
                currentDefinition.compilerVersion || "not_recorded"
              }}</code>
            </dd>
          </div>
          <div>
            <dt>Content hash</dt>
            <dd>
              <code>{{
                currentDefinition.canonicalContentHash || "not_recorded"
              }}</code>
            </dd>
          </div>
        </dl>
      </details>
              <section class="workflow-version-work" data-published-technical-source>
                <ng-container
                  *ngIf="technicalDocumentEntry() as entry; else originalDocumentUnavailable"
                >
                  <details
                    #originalDocument
                    data-published-original-document
                    [open]="technicalDocumentOpen()"
                    (toggle)="reconcileTechnicalDocument(originalDocument)"
                  >
                    <summary
                      (click)="toggleTechnicalDocument($event, entry, originalDocument)"
                    >
                      Original saved document
                    </summary>
                    <pre
                      *ngIf="technicalDocumentOpen()"
                      data-published-original-document-json
                      >{{ savedWorkflow.originalDocument | json }}</pre
                    >
                  </details>
                </ng-container>
                <ng-template #originalDocumentUnavailable>
                  <div
                    *ngIf="savedWorkflow.state !== 'available'"
                    role="status"
                    data-published-original-document-unavailable
                  >
                    <p *ngFor="let issue of savedWorkflow.issues">{{ issue }}</p>
                  </div>
                </ng-template>
              </section>
            </ng-container>
          </section>
        </ui-tab-group>
        </ng-container>
      </ng-container>
      </div>
      <ng-container *ngIf="statusOpen() && statusView() === 'setup'">
        <ng-container [ngTemplateOutlet]="publishedStatusTemplate" [ngTemplateOutletContext]="{ $implicit: publishedStatus() }" />
      </ng-container>
    </section>

    <section
      id="workflow-version-workbench-panel-review"
      class="workflow-version-workbench__panel"
      role="region"
      aria-labelledby="workflow-version-workbench-tab-review"
      [hidden]="activeView !== 'review'"
    >
      <div class="workflow-version-workbench__panel-heading">
        <div>
          <h3 #reviewHeading tabindex="-1">Availability</h3>
          <p>
            Choose availability for new process runs. Existing runs keep their
            current version.
          </p>
        </div>
        <span>Saved version &middot; read only</span>
      </div>

      <section
        class="workflow-version-review__context"
        aria-labelledby="workflow-version-review-context-title"
        data-availability-scope
      >
        <h4 id="workflow-version-review-context-title">Scope and configuration</h4>
        <dl>
          <div>
            <dt>Subject</dt>
            <dd>
              {{
                currentDefinition.subjectTypeCode
                  ? formatCode(currentDefinition.subjectTypeCode)
                  : "Independent process run"
              }}
            </dd>
          </div>
          <div>
            <dt>Saved scope</dt>
            <dd>{{ documentScopeLabel() }}</dd>
          </div>
          <div>
            <dt>Configuration</dt>
            <dd>{{ configurationLabel() }}</dd>
          </div>
          <div>
            <dt>Version</dt>
            <dd>{{ currentDefinition.definitionVersionCode }}</dd>
          </div>
        </dl>
        <p>
          Availability changes do not edit the saved document or move existing runs to
          another version.
        </p>
        <details>
          <summary>Saved registry window</summary>
          <p>{{ formatEffectiveWindow() }}</p>
          <p class="workflow-version-review__window-note">
            Dates shown in {{ reviewTimeZone }}. This saved window is separate from
            activation history; current availability is evaluated by the server.
          </p>
        </details>
      </section>

      <div
        *ngIf="newlyPublished && !currentDefinition.isExecutable"
        class="workflow-version-review__published"
        role="status"
        data-testid="workflow-version-publication-success"
      >
        <i class="pi pi-check-circle" aria-hidden="true"></i>
        <div>
          <strong
            >Published
            {{ currentDefinition.displayName || "workflow" }}.</strong
          >
          <p
            *ngIf="
              currentDefinition.subjectTypeCode === 'hearing_cycle' && canTest;
              else publishedNextAction
            "
          >
            Use More → Run eligibility test to try this version with
            fresh cases. Testing leaves the live version selection unchanged.
          </p>
          <ng-template #publishedNextAction>
            <p
              *ngIf="
                reviewPresentation()?.nextAction?.title ===
                  'Activate this version';
                else publishedAvailability
              "
            >
              Activate it below to let people start using it.
            </p>
          </ng-template>
          <ng-template #publishedAvailability
            ><p>
              Review availability below to see when people can use this version.
            </p></ng-template
          >
        </div>
      </div>

      <phms-workflow-definition-lifecycle-actions
        [managedVersionActions]="true"
        [version]="currentDefinition"
        [versionSourceContext]="availabilitySourceContext"
        [currentVersionSourceContext]="currentAvailabilitySourceContext"
        [versionOwnerContext]="availabilityOwnerContext"
        [currentVersionOwnerContext]="currentAvailabilityOwnerContext"
        [interactionBlocked]="loading || interactionBlocked || activeView !== 'review'"
        (operationInProgressChange)="lifecycleInProgress.set($event)"
        (versionChanged)="lifecycleChanged.emit()"
      ></phms-workflow-definition-lifecycle-actions>

      <details
        class="workflow-version-workbench__technical"
        [open]="reportedStatusOpen()"
        (toggle)="reportedStatusOpen.set($any($event.target).open)"
        data-testid="workflow-version-reported-status"
      >
        <summary #reportedStatusSummary>
          Reported status &amp; technical evidence
        </summary>
        <p>
          Exact returned values for this version. Runtime and eligibility are
          current evaluations, not saved validation results.
        </p>
        <dl>
          <div>
            <dt>Workflow reference</dt>
            <dd>
              <code>{{ currentDefinition.workflowCode }}</code>
            </dd>
          </div>
          <div>
            <dt>Registry status</dt>
            <dd>
              <code>{{ currentDefinition.statusCode }}</code>
            </dd>
          </div>
          <div>
            <dt>Current runtime binding</dt>
            <dd>
              <code>{{ currentDefinition.runtimeBindingCode }}</code>
            </dd>
          </div>
          <div>
            <dt>Current validation status</dt>
            <dd>
              <code>{{ currentDefinition.validationStatusCode }}</code>
            </dd>
          </div>
          <div>
            <dt>Current eligibility evaluation</dt>
            <dd>
              <code>{{ currentDefinition.effectiveEligibilityCode }}</code>
            </dd>
          </div>
          <div>
            <dt>Currently executable</dt>
            <dd>
              <code>{{ currentDefinition.isExecutable }}</code>
            </dd>
          </div>
          <div>
            <dt>Returned restriction codes</dt>
            <dd>
              <code
                *ngFor="
                  let reason of currentDefinition.executabilityReasonCodes
                "
                >{{ reason }}</code
              >
              <span *ngIf="!currentDefinition.executabilityReasonCodes?.length"
                >None returned</span
              >
            </dd>
          </div>
          <div>
            <dt>Saved window start (UTC)</dt>
            <dd>
              <code>{{
                currentDefinition.effectiveStartUtc || "not_recorded"
              }}</code>
            </dd>
          </div>
          <div>
            <dt>Saved window end (UTC)</dt>
            <dd>
              <code>{{
                currentDefinition.effectiveEndUtc || "not_recorded"
              }}</code>
            </dd>
          </div>
          <div>
            <dt>Document provenance</dt>
            <dd>{{ definitionDocumentStatusLabel() }}</dd>
          </div>
          <div>
            <dt>Document status</dt>
            <dd>
              <code>{{ currentDefinition.definitionDocumentStatusCode }}</code>
            </dd>
          </div>
          <div>
            <dt>Document issue codes</dt>
            <dd>
              <code *ngFor="let issue of definitionDocumentIssueCodes()">{{
                issue
              }}</code>
              <span *ngIf="!definitionDocumentIssueCodes().length"
                >None returned</span
              >
            </dd>
          </div>
        </dl>
      </details>

      <details
        class="workflow-version-workbench__technical workflow-version-workbench__tools"
        [open]="diagnosticsOpen()"
        (toggle)="setDiagnosticsOpen($any($event.target).open)"
        data-testid="workflow-version-diagnostics"
      >
        <summary>Diagnostics &amp; testing</summary>
        <p>
          Support tools for live workflow records and synthetic component
          previews.
        </p>
        @if (diagnosticsLoaded() && activeView === "review") {
          @if (
            ["1.3", "1.4", "1.5", "1.6", "1.7", "1.8", "1.9", "1.10"].includes(
              definitionDocument()?.payloadSchemaVersion ?? ""
            )
          ) {
            <phms-workflow-execution-panel [definition]="currentDefinition" />
          }
          <phms-workspace-host-preview />
        }
      </details>
    </section>
  </ng-container>

  <ng-template #noDefinition>
    <p class="workflow-version-workbench__empty" role="status">
      No workflow version is selected.
    </p>
  </ng-template>
</section>

<ng-template #publishedWorkSection let-section>
  <div class="workflow-version-work__section" [attr.data-published-work-section]="section.id">
    <p *ngIf="publishedWorkSectionMessage(section)" role="status">{{ publishedWorkSectionMessage(section) }}</p>
    <p *ngFor="let issue of section.issues" role="status">{{ issue }}</p>
    @for (row of section.rows; track row.id) {
      <article class="workflow-version-work__row" [attr.data-saved-work-row]="row.id">
        <h5>{{ row.label }}</h5>
        <p *ngIf="row.description">{{ row.description }}</p>
        <p *ngFor="let issue of row.issues" class="workflow-version-work__issue">{{ issue }}</p>
        <details class="workflow-version-work__settings">
          <summary>Inspect saved configuration</summary>
          <dl class="workflow-version-work__facts">
            @for (fact of row.fields; track fact.id) {
              <div [attr.data-saved-work-fact]="fact.id"><dt>{{ fact.label }}</dt><dd>{{ fact.value }}
                <small *ngFor="let issue of fact.issues">{{ issue }}</small>
              </dd></div>
            }
          </dl>
          @for (group of row.groups; track group.id) {
            <details class="workflow-version-work__group" [attr.data-saved-work-group]="group.id">
              <summary>{{ group.label }}</summary>
              <p *ngIf="publishedWorkSectionMessage(group)">{{ publishedWorkSectionMessage(group) }}</p>
              <p *ngFor="let issue of group.issues">{{ issue }}</p>
              @for (item of group.rows; track item.id) {
                <section class="workflow-version-work__detail-row" [attr.data-saved-work-detail]="item.id">
                  <h6>{{ item.label }}</h6>
                  <p *ngFor="let issue of item.issues">{{ issue }}</p>
                  <dl class="workflow-version-work__facts">
                    @for (fact of item.fields; track fact.id) {
                      <div [attr.data-saved-work-fact]="fact.id"><dt>{{ fact.label }}</dt><dd>{{ fact.value }}
                        <small *ngFor="let issue of fact.issues">{{ issue }}</small>
                      </dd></div>
                    }
                  </dl>
                </section>
              }
              <details class="workflow-version-work__raw"><summary>Original saved group</summary><pre>{{ group.raw | json }}</pre></details>
            </details>
          }
          <details class="workflow-version-work__raw"><summary>Original saved entry</summary><pre>{{ row.raw | json }}</pre></details>
        </details>
      </article>
    }
    <details *ngIf="section.state === 'retained-unknown' || !section.rows.length" class="workflow-version-work__raw" data-published-work-section-raw>
      <summary>Original saved section</summary><pre>{{ section.raw | json }}</pre>
    </details>
  </div>
</ng-template>

<ng-template #publishedPresentationFacts let-fields>
  <dl *ngIf="fields.length" class="workflow-version-work__facts">
    @for (fact of fields; track fact.id) {
      <div [attr.data-saved-presentation-fact]="fact.id">
        <dt>{{ fact.label }}</dt>
        <dd>
          {{ fact.value
          }}<small *ngFor="let issue of fact.issues">{{ issue }}</small>
        </dd>
      </div>
    }
  </dl>
</ng-template>
<ng-template #publishedPresentationGroup let-group>
  <details
    class="workflow-version-work__group"
    [attr.data-saved-presentation-group]="group.id"
  >
    <summary>{{ group.label }}</summary>
    <p *ngIf="publishedWorkSectionMessage(group)" role="status">
      {{ publishedWorkSectionMessage(group) }}
    </p>
    <p *ngFor="let issue of group.issues" role="status">{{ issue }}</p>
    @for (item of group.rows; track item.id) {
      <section
        class="workflow-version-work__detail-row"
        [attr.data-saved-presentation-detail]="item.id"
      >
        <h6>{{ item.label }}</h6>
        <p *ngFor="let issue of item.issues">{{ issue }}</p>
        <ng-container
          [ngTemplateOutlet]="publishedPresentationFacts"
          [ngTemplateOutletContext]="{ $implicit: item.fields }"
        />
      </section>
    }
    <details class="workflow-version-work__raw">
      <summary>Original saved group</summary>
      <pre>{{ group.raw | json }}</pre>
    </details>
  </details>
</ng-template>
<ng-template #publishedPresentationSection let-section>
  <div
    class="workflow-version-work__section"
    [attr.data-published-presentation-section]="section.id"
  >
    <p *ngIf="publishedWorkSectionMessage(section)" role="status">
      {{ publishedWorkSectionMessage(section) }}
    </p>
    <p *ngFor="let issue of section.issues" role="status">{{ issue }}</p>
    <ng-container
      [ngTemplateOutlet]="publishedPresentationFacts"
      [ngTemplateOutletContext]="{ $implicit: section.fields }"
    />
    @for (group of section.groups; track group.id) {
      <ng-container
        [ngTemplateOutlet]="publishedPresentationGroup"
        [ngTemplateOutletContext]="{ $implicit: group }"
      />
    }
    @for (row of section.rows; track row.id) {
      <article
        class="workflow-version-work__row"
        [attr.data-saved-presentation-row]="row.id"
      >
        <h5>{{ row.label }}</h5>
        <p *ngIf="row.description">{{ row.description }}</p>
        <p
          *ngFor="let issue of row.issues"
          class="workflow-version-work__issue"
        >
          {{ issue }}
        </p>
        <details class="workflow-version-work__settings">
          <summary>Inspect saved configuration</summary>
          <ng-container
            [ngTemplateOutlet]="publishedPresentationFacts"
            [ngTemplateOutletContext]="{ $implicit: row.fields }"
          />
          @for (group of row.groups; track group.id) {
            <ng-container
              [ngTemplateOutlet]="publishedPresentationGroup"
              [ngTemplateOutletContext]="{ $implicit: group }"
            />
          }
          <details class="workflow-version-work__raw">
            <summary>Original saved entry</summary>
            <pre>{{ row.raw | json }}</pre>
          </details>
        </details>
      </article>
    }
    <details
      *ngIf="
        section.state === 'retained-unknown' ||
        (!section.rows.length &&
          !section.fields.length &&
          !section.groups.length)
      "
      class="workflow-version-work__raw"
      data-published-presentation-section-raw
    >
      <summary>Original saved section</summary>
      <pre>{{ section.raw | json }}</pre>
    </details>
  </div>
</ng-template>

<ng-template #publishedStatusTemplate let-status>
  <section
    *ngIf="status"
    class="workflow-version-work"
    aria-labelledby="published-status-title"
    data-published-status
    [attr.data-published-status-scope]="status.target.kind"
  >
    <header class="workflow-version-work__header">
      <button
        uiButton
        type="button"
        [text]="true"
        data-back-from-published-status
        [disabled]="loading || interactionBlocked || lifecycleInProgress()"
        (click)="backFromPublishedStatus(status.context)"
      >
        <i class="pi pi-arrow-left" aria-hidden="true"></i>
        {{
          status.target.kind === 'workflow'
            ? 'Back to Workflow settings'
            : 'Back to process map'
        }}
      </button>
      <div>
        <p>
          {{ status.provenance.workflowCode }} ·
          {{ status.provenance.definitionVersionCode }} ·
          {{
            status.target.kind === 'workflow' ? 'Whole workflow' : 'Saved node'
          }}
        </p>
        <h3 #publishedStatusHeading id="published-status-title" tabindex="-1">
          {{ status.owner?.label || 'Labels and rules' }}
        </h3>
        <p>
          Saved business labels, status changes and decisions. Current status,
          decisions and notification delivery are separate.
        </p>
      </div>
    </header>
    <details class="workflow-version-work__technical">
      <summary>Saved version identity</summary>
      <p>{{ status.provenance.statement }}</p>
      <dl class="workflow-version-work__facts">
        <div>
          <dt>Publication ID</dt>
          <dd>{{ status.provenance.id }}</dd>
        </div>
        <div>
          <dt>Registry document status</dt>
          <dd>{{ status.provenance.documentStatusCode }}</dd>
        </div>
        <div>
          <dt>Reported canonical hash</dt>
          <dd>
            <code>{{ status.provenance.canonicalContentHash }}</code>
          </dd>
        </div>
        <div>
          <dt>Payload / compiler</dt>
          <dd>
            {{ status.provenance.payloadSchemaVersion }} /
            {{ status.provenance.compilerVersion }}
          </dd>
        </div>
      </dl>
      <ul *ngIf="status.provenance.issueCodes.length">
        <li *ngFor="let code of status.provenance.issueCodes">
          <code>{{ code }}</code>
        </li>
      </ul>
    </details>
    <div
      *ngIf="status.state === 'unavailable'"
      role="status"
      data-published-status-unavailable
    >
      <p *ngFor="let issue of status.issues">{{ issue }}</p>
    </div>
    <ng-container *ngIf="status.state === 'available'">
      <p *ngFor="let issue of status.issues" role="status">{{ issue }}</p>
      <ng-container *ngIf="status.owner as owner">
        <p *ngFor="let issue of owner.issues" role="status">{{ issue }}</p>
        <details
          class="workflow-version-work__raw"
          data-published-status-owner-raw
        >
          <summary>
            {{
              status.target.kind === 'workflow'
                ? 'Original saved status and check declarations'
                : 'Original saved node'
            }}
          </summary>
          <pre>{{ owner.raw | json }}</pre>
        </details>
      </ng-container>
      <ui-tab-group
        class="workflow-version-work__tabs"
        [stretchTabs]="false"
        [navigationTabs]="statusTabs"
        [activeTabId]="statusTask()"
        navigationAriaLabel="Saved labels and rules sections"
        (tabRequested)="selectPublishedStatusTask($event, status.context)"
      >
        <section
          class="workflow-version-work__body"
          aria-labelledby="published-status-task-title"
          [attr.data-published-status-task]="statusTask()"
        >
          <h4
            #publishedStatusTaskHeading
            id="published-status-task-title"
            tabindex="-1"
          >
            {{ statusTaskLabel() }}
          </h4>
          <ng-container
            [ngTemplateOutlet]="publishedWorkSection"
            [ngTemplateOutletContext]="{
              $implicit: status.sections[statusTask()],
            }"
          />
        </section>
      </ui-tab-group>
    </ng-container>
  </section>
</ng-template>
